/**
 * Unit Converter Utility
 *
 * This utility provides conversion functions for various unit types used in the biometric questionnaire.
 * It handles conversions between different units and normalizes values for validation purposes.
 */

// ==================== Type Definitions ====================

export type TemperatureUnit = "°C" | "°F" | "°K";
export type TimeUnit = "seconds" | "minutes" | "hours";
export type FrequencyUnit = "daily" | "weekly" | "monthly" | "yearly" | "one-off" | "biannually";
export type MagneticIntensityUnit = "mT" | "T" | "G";
export type WeightUnit = "kgs" | "lbs" | "stones";
export type DistanceUnit = "m" | "km" | "miles" | "yards";
export type LengthUnit = "cm" | "inches";
export type ProcessingTimeUnit = "hour(s)" | "day(s)" | "week(s)" | "month(s)";

export type UnitType =
  | TemperatureUnit
  | TimeUnit
  | FrequencyUnit
  | MagneticIntensityUnit
  | WeightUnit
  | DistanceUnit
  | LengthUnit
  | ProcessingTimeUnit;

// ==================== Conversion Functions ====================

/**
 * Determines if a unit type requires conversion calculations
 * @param unitList - Array of available units
 * @returns true if conversion is needed, false otherwise
 */
export const requiresConversion = (unitList: string[]): boolean => {
  // Single unit attributes don't need conversion
  if (!unitList || unitList.length <= 1) {
    return false;
  }

  // Check if this is a multi-unit attribute that requires calculations
  const conversionUnits = [
    // Temperature
    "°C", "°F", "°K",
    // Time
    "seconds", "minutes", "hours",
    // Frequency
    "daily", "weekly", "monthly", "yearly", "one-off", "biannually",
    // Magnetic Intensity
    "mT", "T", "G",
    // Weight
    "kgs", "lbs", "stones",
    // Distance
    "m", "km", "miles", "yards",
    // Length
    "cm", "inches",
    // Processing Time
    "hour(s)", "day(s)", "week(s)", "month(s)",
  ];

  return unitList.some(unit => conversionUnits.includes(unit));
};

/**
 * Temperature Conversions
 * Base unit: °C (Celsius)
 */
export const convertTemperature = (
  value: number,
  fromUnit: TemperatureUnit,
  toUnit: TemperatureUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to Celsius first
  let celsius: number;
  switch (fromUnit) {
    case "°C":
      celsius = value;
      break;
    case "°F":
      celsius = (value - 32) / 1.8;
      break;
    case "°K":
      celsius = value - 273.15;
      break;
  }

  // Convert from Celsius to target unit
  switch (toUnit) {
    case "°C":
      return celsius;
    case "°F":
      return celsius * 1.8 + 32;
    case "°K":
      return celsius + 273.15;
  }
};

/**
 * Time Duration Conversions
 * Base unit: minutes
 */
export const convertTime = (
  value: number,
  fromUnit: TimeUnit,
  toUnit: TimeUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to minutes first
  let minutes: number;
  switch (fromUnit) {
    case "seconds":
      minutes = value / 60;
      break;
    case "minutes":
      minutes = value;
      break;
    case "hours":
      minutes = value * 60;
      break;
  }

  // Convert from minutes to target unit
  switch (toUnit) {
    case "seconds":
      return minutes * 60;
    case "minutes":
      return minutes;
    case "hours":
      return minutes / 60;
  }
};

/**
 * Frequency Conversions
 * Base unit: weekly
 */
export const convertFrequency = (
  value: number,
  fromUnit: FrequencyUnit,
  toUnit: FrequencyUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Handle special cases
  if (fromUnit === "one-off" || toUnit === "one-off") {
    return value; // one-off doesn't convert
  }

  // Convert to weekly first
  let weekly: number;
  switch (fromUnit) {
    case "daily":
      weekly = value * 7;
      break;
    case "weekly":
      weekly = value;
      break;
    case "monthly":
      weekly = value / 4;
      break;
    case "yearly":
      weekly = value / 52;
      break;
    case "biannually":
      weekly = value / 26;
      break;
    default:
      weekly = value;
  }

  // Convert from weekly to target unit
  switch (toUnit) {
    case "daily":
      return weekly / 7;
    case "weekly":
      return weekly;
    case "monthly":
      return weekly * 4;
    case "yearly":
      return weekly * 52;
    case "biannually":
      return weekly * 26;
    default:
      return weekly;
  }
};

/**
 * Magnetic Intensity Conversions
 * Base unit: mT (milliTesla)
 */
export const convertMagneticIntensity = (
  value: number,
  fromUnit: MagneticIntensityUnit,
  toUnit: MagneticIntensityUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to mT first
  let milliTesla: number;
  switch (fromUnit) {
    case "mT":
      milliTesla = value;
      break;
    case "T":
      milliTesla = value * 1000;
      break;
    case "G":
      milliTesla = value / 10;
      break;
  }

  // Convert from mT to target unit
  switch (toUnit) {
    case "mT":
      return milliTesla;
    case "T":
      return milliTesla / 1000;
    case "G":
      return milliTesla * 10;
  }
};

/**
 * Weight Conversions
 * Base unit: kgs (kilograms)
 */
export const convertWeight = (
  value: number,
  fromUnit: WeightUnit,
  toUnit: WeightUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to kgs first
  let kilograms: number;
  switch (fromUnit) {
    case "kgs":
      kilograms = value;
      break;
    case "lbs":
      kilograms = value * 0.453592;
      break;
    case "stones":
      kilograms = value * 6.35029;
      break;
  }

  // Convert from kgs to target unit
  switch (toUnit) {
    case "kgs":
      return kilograms;
    case "lbs":
      return kilograms / 0.453592;
    case "stones":
      return kilograms / 6.35029;
  }
};

/**
 * Distance Conversions
 * Base unit: meters
 */
export const convertDistance = (
  value: number,
  fromUnit: DistanceUnit,
  toUnit: DistanceUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to meters first
  let meters: number;
  switch (fromUnit) {
    case "m":
      meters = value;
      break;
    case "km":
      meters = value * 1000;
      break;
    case "miles":
      meters = value * 1609.34;
      break;
    case "yards":
      meters = value * 0.9144;
      break;
  }

  // Convert from meters to target unit
  switch (toUnit) {
    case "m":
      return meters;
    case "km":
      return meters / 1000;
    case "miles":
      return meters / 1609.34;
    case "yards":
      return meters / 0.9144;
  }
};

/**
 * Length Conversions (Height)
 * Base unit: cm (centimeters)
 * Formula: cm = inches * 2.54
 */
export const convertLength = (
  value: number,
  fromUnit: LengthUnit,
  toUnit: LengthUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to cm first
  let centimeters: number;
  switch (fromUnit) {
    case "cm":
      centimeters = value;
      break;
    case "inches":
      centimeters = value * 2.54;
      break;
  }

  // Convert from cm to target unit
  switch (toUnit) {
    case "cm":
      return centimeters;
    case "inches":
      return centimeters / 2.54;
  }
};

/**
 * Processing Time Conversions
 * Base unit: day(s)
 */
export const convertProcessingTime = (
  value: number,
  fromUnit: ProcessingTimeUnit,
  toUnit: ProcessingTimeUnit
): number => {
  if (fromUnit === toUnit) return value;

  // Convert to days first
  let days: number;
  switch (fromUnit) {
    case "hour(s)":
      days = value / 24;
      break;
    case "day(s)":
      days = value;
      break;
    case "week(s)":
      days = value * 7;
      break;
    case "month(s)":
      days = value * 30;
      break;
  }

  // Convert from days to target unit
  switch (toUnit) {
    case "hour(s)":
      return days * 24;
    case "day(s)":
      return days;
    case "week(s)":
      return days / 7;
    case "month(s)":
      return days / 30;
  }
};

// ==================== Generic Converter ====================

/**
 * Generic unit converter that automatically detects the unit type
 * and applies the appropriate conversion function
 */
export const convertValue = (
  value: number | string,
  fromUnit: string,
  toUnit: string
): number => {
  // Handle null/undefined units
  if (!fromUnit || !toUnit) {
    return typeof value === 'string' ? parseFloat(value) : value;
  }

  // Trim whitespace and ensure we're working with numbers
  const trimmedFromUnit = fromUnit.trim();
  const trimmedToUnit = toUnit.trim();
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (trimmedFromUnit === trimmedToUnit) {
    return numericValue;
  }

  let convertedValue = numericValue;

  // Temperature
  if (["°C", "°F", "°K"].includes(trimmedFromUnit)) {
    convertedValue = convertTemperature(numericValue, trimmedFromUnit as TemperatureUnit, trimmedToUnit as TemperatureUnit);
  }

  // Time
  else if (["seconds", "minutes", "hours"].includes(trimmedFromUnit)) {
    convertedValue = convertTime(numericValue, trimmedFromUnit as TimeUnit, trimmedToUnit as TimeUnit);
  }

  // Frequency
  else if (["daily", "weekly", "monthly", "yearly", "one-off", "biannually"].includes(trimmedFromUnit)) {
    convertedValue = convertFrequency(numericValue, trimmedFromUnit as FrequencyUnit, trimmedToUnit as FrequencyUnit);
  }

  // Magnetic Intensity
  else if (["mT", "T", "G"].includes(trimmedFromUnit)) {
    convertedValue = convertMagneticIntensity(
      numericValue,
      trimmedFromUnit as MagneticIntensityUnit,
      trimmedToUnit as MagneticIntensityUnit
    );
  }

  // Weight - Commented out for future use
  // else if (["kgs", "lbs", "stones"].includes(trimmedFromUnit)) {
  //   convertedValue = convertWeight(numericValue, trimmedFromUnit as WeightUnit, trimmedToUnit as WeightUnit);
  // }

  // Distance - Commented out for future use
  // else if (["m", "km", "miles", "yards"].includes(trimmedFromUnit)) {
  //   convertedValue = convertDistance(numericValue, trimmedFromUnit as DistanceUnit, trimmedToUnit as DistanceUnit);
  // }

  // Length (Height)
  else if (["cm", "inches"].includes(trimmedFromUnit)) {
    convertedValue = convertLength(numericValue, trimmedFromUnit as LengthUnit, trimmedToUnit as LengthUnit);
  }

  // Processing Time
  else if (["hour(s)", "day(s)", "week(s)", "month(s)"].includes(trimmedFromUnit)) {
    convertedValue = convertProcessingTime(
      numericValue,
      trimmedFromUnit as ProcessingTimeUnit,
      trimmedToUnit as ProcessingTimeUnit
    );
  }

  return convertedValue;
};

/**
 * Converts a range (min/max) from one unit to another
 */
export const convertRange = (
  min: number,
  max: number,
  fromUnit: string,
  toUnit: string
): { min: number; max: number } => {
  return {
    min: convertValue(min, fromUnit, toUnit),
    max: convertValue(max, fromUnit, toUnit),
  };
};

/**
 * Formats a number to an appropriate precision based on the value
 * Uses ceil for values below 1, round for values >= 1
 */
export const formatConvertedValue = (value: number): number => {
  if (value < 1 && value > 0) {
    return Math.ceil(value);
  }
  return Math.round(value);
};
